import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { invoiceTxnActions, httpRequestsOnSuccessActions } from 'store';
import { CreateChancel, ErrMessage, SelectAutocompleteMultiple, NumericFormatCustom, TextareaCustom } from 'components';
import { FindLoad, FindSuccessItem, FindErrorItem, hooksForTable, useModal } from 'utils';
import { reasonOptionsByType, txnTypeOptions } from './constants';

const CREATE_TYPE = 'CREATE_INVOICE_TXN';


const AddTransactionForm = ({ invoice, control, watch }) => {
    const { addSignToValueFromStart, getValueByFixedNumber } = hooksForTable;
    const currentBalance = invoice?.balance || 0;
    const txnAmount = watch('amount') || 0;
    const newBalance = Math.max(0, currentBalance - txnAmount);
    const selectedType = watch('type');
    const typeName = txnTypeOptions.find((t) => t.id === selectedType)?.name || 'Transaction';
    const reasonOptions = reasonOptionsByType[selectedType] || [];

    return (
        <div className="add-transaction-form">
            <div className="add-transaction-invoice-info">
                <div className="add-transaction-invoice-info-col">
                    <p className="add-transaction-info-text">
                        Invoice ID: <span className="add-transaction-info-value">{invoice?.displayId}</span>
                    </p>
                    <p className="add-transaction-info-text">
                        Client Name:{' '}
                        <span className="add-transaction-info-value">{invoice?.client?.clientFullName || '--'}</span>
                    </p>
                </div>

                <div className="add-transaction-invoice-info-col add-transaction-invoice-info-col--right">
                    <p className="add-transaction-info-text">
                        Invoice Total:{' '}
                        <span className="add-transaction-info-value">
                            {addSignToValueFromStart(getValueByFixedNumber(invoice?.totalAMT))}
                        </span>
                    </p>
                    <p className="add-transaction-info-text">
                        Current Balance:{' '}
                        <span className="add-transaction-info-value">
                            {addSignToValueFromStart(getValueByFixedNumber(currentBalance))}
                        </span>
                    </p>
                </div>
            </div>

            <SelectAutocompleteMultiple
                name="type"
                label="Transaction Type*"
                control={control}
                options={txnTypeOptions}
                rules={{ required: 'Transaction type is required.' }}
                renderValue={(option) => option?.name}
            />

            <NumericFormatCustom
                name="amount"
                label="Amount*"
                prefix={'$'}
                thousandSeparator={true}
                fixedDecimalScale={true}
                allowNegative={true}
                decimalScale={2}
                control={control}
                rules={{
                    required: 'Amount is required.',
                    validate: (value) => {
                        // if (!value || value <= 0) return 'Amount must be greater than 0.';
                        if (value > currentBalance) return 'Amount cannot exceed current balance.';
                        return true;
                    },
                }}
            />

            <SelectAutocompleteMultiple
                name="reason"
                label="Reason"
                control={control}
                options={reasonOptions}
                // rules={{ required: 'Reason is required.' }}
                renderValue={(option) => option?.name}
                disabled={!selectedType}
            />

            <TextareaCustom
                control={control}
                name="notes"
                label="Notes (Optional)"
                variant={'outlined'}
            />

            <div className="add-transaction-summary">
                <div className="add-transaction-summary-row" style={{marginBottom: 16}}>
                    <p className="add-transaction-summary-label">Current Balance</p>
                    <p className="add-transaction-summary-value">
                        {addSignToValueFromStart(getValueByFixedNumber(+currentBalance))}
                    </p>
                </div>
                <div className="add-transaction-summary-row">
                    <p className="add-transaction-summary-label">{typeName}</p>
                    <p className="add-transaction-summary-value">
                        {addSignToValueFromStart(getValueByFixedNumber(+txnAmount))}
                    </p>
                </div>
                <div className="add-transaction-summary-total">
                    <p className="add-transaction-summary-total-label">New Remaining Balance</p>
                    <p className="add-transaction-summary-total-value">
                        {addSignToValueFromStart(getValueByFixedNumber(+newBalance))}
                    </p>
                </div>
            </div>
        </div>
    );
};

export const AddTransactionModal = ({ invoice, afterSuccess }) => {
    const dispatch = useDispatch();
    const { close } = useModal();
    const loader = FindLoad(CREATE_TYPE);
    const success = FindSuccessItem(CREATE_TYPE);
    const backError = FindErrorItem(CREATE_TYPE);

    const { handleSubmit, control, watch } = useForm();

    useEffect(() => {
        if (success) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CREATE_TYPE));
            afterSuccess?.();
        }
    }, [success]);

    const handleCreate = (data) => {
        const params = {
            ...data
        }
        dispatch(invoiceTxnActions.createInvoiceTxn(invoice?.id, params));
    };

    return (
        <div style={{ width: 520 }}>
            <p className="modal-header-title">Add Financial Transaction</p>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper">
                    <AddTransactionForm invoice={invoice} control={control} watch={watch} />
                </div>
                <ErrMessage
                    style={{ marginBottom: '12px' }}
                    text={backError?.error || ''}
                />
                <CreateChancel
                    type="submit"
                    create={'Save Transaction'}
                    chancel={'Cancel'}
                    onClose={close}
                    loader={!!loader?.length}
                    buttonWidth={'100%'}
                    cancelRight={'24px'}
                />
            </form>
        </div>
    );
};
